<template>
  <div class="ws-billing">
    <div class="ws-billing-spinner" v-if="loading">
      <fts-spinner show />
    </div>

    <template v-else-if="summary">
      <!-- low-credit banner -->
      <div class="wsb-banner" v-if="summary.lowCredit">
        ⚠ AI credits are running low — top up or upgrade to keep GNey and automations running.
      </div>

      <!-- current plan -->
      <div class="wsb-plan-card">
        <div class="wsb-plan-head">
          <div class="wsb-plan-name">{{ planLabel }}</div>
          <div class="wsb-status" :class="statusClass">{{ statusLabel }}</div>
        </div>
        <div class="wsb-plan-sub" v-if="renewalLine">{{ renewalLine }}</div>
        <!-- actor seats (pricing spec §16): one acting identity each — a person, an automation or
             an AI over MCP; only seats can connect externally -->
        <div class="wsb-seats" v-if="seats">
          <div class="wsb-credit-row">
            <span class="wsb-credit-label">Actor seats</span>
            <span class="wsb-credit-value"
              >{{ seatsLine }}
              <span class="wsb-credit-sub">{{ seatsSub }}</span></span
            >
          </div>
          <div class="wsb-plan-sub">Only actor seats can connect Claude, ChatGPT, n8n, Zapier or the API.</div>
        </div>
        <div class="wsb-actions" v-if="summary.hasCustomer && !isManual">
          <fts-btn
            color="primary"
            box-shadow-none
            text-transform-init
            :disabled="busy"
            @click.native="openPortal"
            >Manage billing</fts-btn
          >
        </div>
      </div>

      <!-- credits: meter + top-up -->
      <div class="wsb-credits">
        <!-- vendor-billed with no allowance: usage is metered and invoiced, never capped, so
             there is nothing to meter against -->
        <div class="wsb-credit-block" v-if="isUncapped">
          <div class="wsb-credit-row">
            <span class="wsb-credit-label">Plan credits</span>
            <span class="wsb-credit-value"
              >Unlimited
              <span class="wsb-credit-sub">usage billed by {{ billedByLabel }}</span></span
            >
          </div>
        </div>
        <!-- no monthly allowance (plans ship zero included credits, spec §13): the purchased
             balance IS the balance, so it takes the headline instead of an always-empty meter -->
        <div class="wsb-credit-block" v-else-if="isPayAsYouGo">
          <div class="wsb-credit-row">
            <span class="wsb-credit-label">AI credits</span>
            <span class="wsb-credit-value"
              >{{ fmt(summary.credits.purchased) }}
              <span class="wsb-credit-sub">pay as you go · never expire</span></span
            >
          </div>
        </div>
        <div class="wsb-credit-block" v-else>
          <div class="wsb-credit-row">
            <span class="wsb-credit-label">Plan credits</span>
            <span class="wsb-credit-value"
              >{{ fmt(summary.credits.plan) }}
              <span class="wsb-credit-sub">of {{ fmt(summary.credits.includedMonthly) }} · {{ resetLine }}</span></span
            >
          </div>
          <div class="wsb-meter">
            <div class="wsb-meter-fill" :class="{ 'is-low': summary.lowCredit }" :style="{ width: meterPct + '%' }"></div>
          </div>
        </div>
        <div class="wsb-credit-row" v-if="!isPayAsYouGo">
          <span class="wsb-credit-label">Top-up credits</span>
          <span class="wsb-credit-value"
            >{{ fmt(summary.credits.purchased) }}
            <span class="wsb-credit-sub">never expire</span></span
          >
        </div>
        <div class="wsb-packs" v-if="!isManual && summary.stripeReady && buyablePacks.length">
          <fts-btn
            v-for="p in buyablePacks"
            :key="p.packKey"
            box-shadow-none
            text-transform-init
            :disabled="busy"
            @click.native="buyPack(p)"
            >+ {{ fmt(p.credits) }}<span class="wsb-pack-price" v-if="money(p.price)"> · {{ money(p.price) }}</span></fts-btn
          >
        </div>
        <div class="wsb-note" v-else-if="isManual">
          Credit top-ups and plan changes are handled by {{ billedByLabel }}.
        </div>
        <div class="wsb-note" v-else>Top-up packs aren't available for online purchase yet.</div>

        <div class="wsb-redeem">
          <input
            class="wsb-redeem-input"
            v-model="redeemCode"
            :disabled="redeeming"
            maxlength="24"
            placeholder="Have a code?"
            aria-label="Voucher code"
            @keyup.enter="redeem"
          />
          <fts-btn
            box-shadow-none
            text-transform-init
            :disabled="redeeming || !redeemCode"
            @click.native="redeem"
            >Redeem</fts-btn
          >
        </div>
      </div>

      <!-- usage — last 30 days -->
      <div class="wsb-section-title">Usage — last 30 days</div>
      <div class="wsb-chart-card">
        <div class="wsb-note" v-if="!hasUsage">No AI usage in this period.</div>
        <template v-else>
          <svg :viewBox="'0 0 ' + chart.W + ' ' + (chart.H + 18)" class="wsb-chart" preserveAspectRatio="none">
            <line :x1="0" :y1="chart.H + 0.5" :x2="chart.W" :y2="chart.H + 0.5" class="wsb-axis" />
            <g v-for="(bar, i) in chartBars" :key="i">
              <title>{{ bar.title }}</title>
              <rect
                v-for="(seg, j) in bar.segs"
                :key="j"
                :x="bar.x"
                :y="seg.y"
                :width="bar.w"
                :height="seg.h"
                :fill="seg.color"
                rx="1"
              />
              <!-- invisible hover target covering the full column -->
              <rect :x="bar.x" y="0" :width="bar.w" :height="chart.H" fill="transparent" />
            </g>
            <text :x="0" :y="chart.H + 13" class="wsb-axis-label">{{ firstDate }}</text>
            <text :x="chart.W" :y="chart.H + 13" class="wsb-axis-label" text-anchor="end">{{ lastDate }}</text>
          </svg>
          <div class="wsb-legend">
            <span v-for="c in categories" :key="c.key" class="wsb-legend-item">
              <span class="wsb-legend-dot" :style="{ background: c.color }"></span>{{ c.label }}
            </span>
            <span class="wsb-legend-total">{{ fmt(usageTotal) }} credits total</span>
          </div>
        </template>
      </div>

      <!-- history -->
      <div class="wsb-section-title">History</div>
      <div class="wsb-history-card">
        <div class="wsb-note" v-if="!tx.items.length">No credit activity yet.</div>
        <template v-else>
          <div class="wsb-tx-row" v-for="t in tx.items" :key="t.id">
            <span class="wsb-tx-date">{{ fmtDate(t.timestamp) }}</span>
            <span class="wsb-tx-label">{{ txLabel(t) }}</span>
            <span class="wsb-tx-delta" :class="{ 'is-neg': t.delta < 0 }">{{ t.delta >= 0 ? "+" : "" }}{{ fmt(t.delta) }}</span>
          </div>
          <div class="wsb-load-more" v-if="tx.items.length < tx.total">
            <fts-btn box-shadow-none text-transform-init :disabled="tx.loadingMore" @click.native="loadMoreTx"
              >Load more</fts-btn
            >
          </div>
        </template>
      </div>

      <!-- upgrade — only while there is no active subscription (after that, the Portal manages
           the plan) and never for dealer-billed workspaces (their dealer owns the plan) -->
      <template v-if="summary.status !== 'active' && !isManual && !hideUpgrade">
        <div class="wsb-section-title">Upgrade this workspace</div>
        <div v-if="!summary.stripeReady || !sellablePlans.length" class="wsb-note">
          Online purchase isn't available yet — contact us to upgrade this workspace.
        </div>
        <template v-else>
          <div class="plan-wrapper" v-for="p in sellablePlans" :key="p.planKey">
            <div class="plan-info">
              <div class="plan-name">{{ cap(p.planKey) }}</div>
              <!-- what the plan costs, in the currency the checkout will charge — a customer
                   must see the price before Stripe does; the credits line only when a plan
                   actually bundles some -->
              <div class="plan-price">
                <div class="wsb-price-tag" v-if="money(p.priceMonthly)">
                  {{ money(p.priceMonthly) }}<span class="wsb-price-unit"> / month</span>
                </div>
                <div class="wsb-price-sub" v-if="money(p.priceYearly)">
                  {{ money(p.priceYearly) }} / year<span v-if="money(p.priceMonthly)"> · 2 months free</span>
                </div>
                <div class="wsb-price-sub" v-if="p.includedCredits > 0">{{ fmt(p.includedCredits) }} AI credits / month</div>
                <div class="wsb-price-sub" v-if="p.includedMembers">
                  {{ p.includedMembers }} users · 1 actor seat<span v-if="p.addOnSlots"> + {{ p.addOnSlots }} add-on slots</span>
                </div>
              </div>
            </div>
            <div class="wsb-buy-btns">
              <fts-btn
                v-if="p.hasMonthly"
                color="primary"
                box-shadow-none
                text-transform-init
                :disabled="busy"
                @click.native="checkout(p, 'month')"
                >Subscribe monthly</fts-btn
              >
              <fts-btn
                v-if="p.hasYearly"
                box-shadow-none
                text-transform-init
                :disabled="busy"
                @click.native="checkout(p, 'year')"
                >Yearly</fts-btn
              >
            </div>
          </div>
        </template>
      </template>
    </template>
  </div>
</template>

<script>
// The full "Plan & Credits" tab (P3): plan card + credit meter/low-credit banner + pack top-ups
// + a dependency-free inline-SVG usage chart + the money-side ledger. The webhook remains the
// source of truth — returns from Stripe only poll the summary until it reflects the event.
import { commonStore } from "../store/commonStore";
import { formatMoney, readBillingReturn, stripBillingReturn } from "../commons/billing";
import FTSBtn from "../elements/FTSBtn";
import FTSSpinner from "../elements/FTSSpinner";

const POLL_ATTEMPTS = 8;
const POLL_INTERVAL_MS = 1500;
const TX_PAGE_SIZE = 20;

const CATEGORIES = [
  { key: "chat", label: "Chat", color: "#5457c9" },
  { key: "design", label: "Design", color: "#0e7a6d" },
  { key: "automation", label: "Automation", color: "#a8650a" },
  { key: "routines", label: "Routines", color: "#7d8894" },
];

export default {
  name: "FTSWorkspaceBilling",
  components: {
    "fts-btn": FTSBtn,
    "fts-spinner": FTSSpinner,
  },
  props: {
    workspaceId: { type: [Number, String], required: true },
    // Set by the settings Plan page, whose own comparison shows every rung and replaces
    // this block. Off by default: the dialog is unchanged.
    hideUpgrade: { type: Boolean, default: false },
  },
  data: () => ({
    loading: true,
    summary: null,
    busy: false,
    usageDays: [],
    tx: { items: [], total: 0, page: 0, loadingMore: false },
    redeemCode: "",
    redeeming: false,
    categories: CATEGORIES,
    chart: { W: 600, H: 110 },
  }),
  computed: {
    seats() {
      return (this.summary && this.summary.seats) || null;
    },
    seatsLine() {
      const s = this.seats;
      if (!s) return "";
      return s.seatLimit != null ? s.held + " of " + s.seatLimit : String(s.held);
    },
    seatsSub() {
      const s = this.seats;
      if (!s) return "";
      if (s.seatLimit == null) return s.included + " included · no slot limit";
      return s.included + " included · " + s.addOnSlots + " add-on slots"
        + (s.extra > 0 ? " · " + s.extra + " extra" : "");
    },
    isManual() {
      // Vendor/dealer/enterprise arrangement billed outside Stripe: no purchase surfaces here.
      return !!(this.summary && this.summary.source === "manual");
    },
    isUncapped() {
      // Manual arrangement with no monthly allowance (existing workspaces default here):
      // metered, never gated — the server sends includedMonthly 0 and lowCredit false.
      return this.isManual && !!this.summary.uncapped;
    },
    billedByLabel() {
      return (this.summary && this.summary.billedBy) || "your vendor";
    },
    isPayAsYouGo() {
      // A plan-ladder workspace whose plan carries no monthly allowance (the launch config —
      // zero included credits everywhere): "0 of 0" on a meter reads as broken, so the
      // purchased balance is the headline instead. Vendor-uncapped workspaces have their own block.
      const c = this.summary && this.summary.credits;
      return !this.isUncapped && !!c && !(Number(c.includedMonthly) > 0);
    },
    planLabel() {
      if (this.summary && this.summary.planName) return this.summary.planName;
      const key = (this.summary && this.summary.planKey) || "free";
      if (key === "free") return "Free plan";
      if (key === "vendor") return "Vendor billing";
      return this.cap(key) + " plan";
    },
    statusLabel() {
      const s = (this.summary && this.summary.status) || "free";
      if (s === "active") return "Active";
      if (s === "past_due") return "Payment issue";
      if (s === "expired") return "Expired";
      if (s === "canceled") return "Canceled";
      return "Free";
    },
    statusClass() {
      const s = (this.summary && this.summary.status) || "free";
      return "is-" + s;
    },
    renewalLine() {
      if (!this.summary) return "";
      if (this.isManual) {
        const by = this.billedByLabel;
        if (this.summary.status === "expired") {
          return "Billed directly by " + by + " — arrangement expired on " + this.fmtDate(this.summary.validUntil) + ".";
        }
        return "Billed directly by " + by
          + (this.summary.validUntil ? " — valid until " + this.fmtDate(this.summary.validUntil) : "") + ".";
      }
      if (this.summary.status === "past_due" && this.summary.graceUntil) {
        return "Payment failed — AI stays on until " + this.fmtDate(this.summary.graceUntil) + ". Update your card in Manage billing.";
      }
      if (!this.summary.currentPeriodEnd) return "";
      return (this.summary.cancelAtPeriodEnd ? "Cancels on " : "Renews on ")
        + this.fmtDate(this.summary.currentPeriodEnd);
    },
    resetLine() {
      if (this.summary && this.summary.status === "active" && this.summary.currentPeriodEnd) {
        return "resets " + this.fmtDate(this.summary.currentPeriodEnd);
      }
      return "resets monthly";
    },
    meterPct() {
      const c = this.summary && this.summary.credits;
      if (!c || !c.includedMonthly) return 0;
      return Math.max(0, Math.min(100, (c.plan / c.includedMonthly) * 100));
    },
    sellablePlans() {
      const plans = (this.summary && this.summary.plans) || [];
      return plans.filter((p) => p.hasMonthly || p.hasYearly);
    },
    buyablePacks() {
      const packs = (this.summary && this.summary.packs) || [];
      return packs.filter((p) => p.hasPrice);
    },
    hasUsage() {
      return this.usageDays.some((d) => (d.credits || 0) > 0);
    },
    usageTotal() {
      return this.usageDays.reduce((sum, d) => sum + (d.credits || 0), 0);
    },
    firstDate() {
      return this.usageDays.length ? this.shortDate(this.usageDays[0].date) : "";
    },
    lastDate() {
      return this.usageDays.length ? this.shortDate(this.usageDays[this.usageDays.length - 1].date) : "";
    },
    chartBars() {
      const days = this.usageDays;
      if (!days.length) return [];
      const { W, H } = this.chart;
      const slot = W / days.length;
      const barWidth = Math.max(2, slot - 2);
      const max = Math.max.apply(null, [1e-6].concat(days.map((d) => d.credits || 0)));

      return days.map((d, i) => {
        let yTop = H;
        const segs = [];
        const parts = [];
        for (const c of CATEGORIES) {
          const value = (d.byCategory && d.byCategory[c.key]) || 0;
          if (value <= 0) continue;
          const h = Math.max(1, (value / max) * (H - 6));
          yTop -= h;
          segs.push({ y: yTop, h, color: c.color });
          parts.push(c.label + " " + this.fmt(value));
        }
        const total = this.fmt(d.credits || 0);
        return {
          x: i * slot + 1,
          w: barWidth,
          segs,
          title: this.shortDate(d.date) + " — " + total + " credits"
            + (parts.length ? " (" + parts.join(", ") + ")" : ""),
        };
      });
    },
  },
  mounted() {
    this.fetchSummary().then((summary) => {
      if (!summary) return;
      this.fetchUsage();
      this.fetchTransactions();
      // Back from Stripe (the shells open this tab on the return — see commons/billing.js): the
      // redirect is only a hint — poll until the webhook lands. A return stamped for another
      // workspace is left alone for that workspace's tab to pick up.
      const ret = readBillingReturn(window.location.search);
      if (!ret) return;
      if (ret.workspaceId && Number(ret.workspaceId) !== Number(this.workspaceId)) return;
      // A connector's Checkout (AutoCount Full) is confirmed on its own page, Settings › AutoCount.
      if (ret.connector) return;
      this.stripBillingQuery();
      if (ret.kind === "success") {
        this.pollUntil(() => this.summary && this.summary.status === "active", "Subscription active — welcome aboard!");
      } else if (ret.kind === "pack") {
        const before = (summary.credits && summary.credits.purchased) || 0;
        this.pollUntil(
          () => this.summary && this.summary.credits && this.summary.credits.purchased > before,
          "Credits added to this workspace."
        );
      }
      // "cancel": nothing happened on Stripe's side — the query is cleaned up, the tab is open.
    });
  },
  methods: {
    api() {
      return commonStore.state.client;
    },
    async fetchSummary() {
      try {
        const res = await this.api().get("/api/billing/summary", {
          params: { workspaceId: this.workspaceId },
        });
        this.summary = res.data;
        // One fetch feeds both this page and anything hosting it.
        this.$emit("summary", this.summary);
      } catch (err) {
        this.summary = null;
        // 404 = feature flag off → the modal falls back to its legacy static subscription block.
        this.$emit("fallback", err && err.response && err.response.status);
      } finally {
        this.loading = false;
      }
      return this.summary;
    },
    async fetchUsage() {
      try {
        const res = await this.api().get("/api/billing/usage/daily", {
          params: { workspaceId: this.workspaceId, days: 30 },
        });
        this.usageDays = (res.data && res.data.days) || [];
      } catch (err) {
        this.usageDays = [];
      }
    },
    async fetchTransactions() {
      try {
        const res = await this.api().get("/api/billing/transactions", {
          params: { workspaceId: this.workspaceId, page: 0, take: TX_PAGE_SIZE },
        });
        this.tx = { items: (res.data && res.data.items) || [], total: (res.data && res.data.total) || 0, page: 0, loadingMore: false };
      } catch (err) {
        this.tx = { items: [], total: 0, page: 0, loadingMore: false };
      }
    },
    async loadMoreTx() {
      this.tx.loadingMore = true;
      try {
        const page = this.tx.page + 1;
        const res = await this.api().get("/api/billing/transactions", {
          params: { workspaceId: this.workspaceId, page, take: TX_PAGE_SIZE },
        });
        this.tx.items = this.tx.items.concat((res.data && res.data.items) || []);
        this.tx.total = (res.data && res.data.total) || this.tx.total;
        this.tx.page = page;
      } catch (err) {
        this.toastError(err);
      } finally {
        this.tx.loadingMore = false;
      }
    },
    async checkout(plan, interval) {
      this.busy = true;
      try {
        const res = await this.api().post("/api/billing/checkout", {
          WorkspaceId: Number(this.workspaceId),
          PlanKey: plan.planKey,
          Interval: interval,
        });
        if (res.data && res.data.url) window.location = res.data.url;
        else this.toastError(null, "Stripe did not return a checkout link.");
      } catch (err) {
        this.toastError(err);
      } finally {
        this.busy = false;
      }
    },
    async redeem() {
      const code = (this.redeemCode || "").trim();
      if (!code) return;
      this.redeeming = true;
      try {
        const res = await this.api().post("/api/billing/redeem", {
          WorkspaceId: Number(this.workspaceId),
          Code: code,
        });
        const credits = (res.data && res.data.credits) || 0;
        this.redeemCode = "";
        // No polling here: unlike Stripe, the grant is already applied when this returns.
        await this.fetchSummary();
        this.fetchTransactions();
        if (this.$toasted && this.$toasted.global && this.$toasted.global.success) {
          this.$toasted.global.success(this.fmt(credits) + " credits added to this workspace.");
        }
      } catch (err) {
        this.toastError(err, "That code couldn't be redeemed.");
      } finally {
        this.redeeming = false;
      }
    },
    async buyPack(pack) {
      this.busy = true;
      try {
        const res = await this.api().post("/api/billing/checkout/credits", {
          WorkspaceId: Number(this.workspaceId),
          PackKey: pack.packKey,
        });
        if (res.data && res.data.url) window.location = res.data.url;
        else this.toastError(null, "Stripe did not return a checkout link.");
      } catch (err) {
        this.toastError(err);
      } finally {
        this.busy = false;
      }
    },
    async openPortal() {
      this.busy = true;
      try {
        const res = await this.api().post("/api/billing/portal", {
          WorkspaceId: Number(this.workspaceId),
        });
        if (res.data && res.data.url) window.location = res.data.url;
        else this.toastError(null, "Stripe did not return a portal link.");
      } catch (err) {
        this.toastError(err);
      } finally {
        this.busy = false;
      }
    },
    async pollUntil(predicate, successMessage) {
      for (let i = 0; i < POLL_ATTEMPTS; i++) {
        if (predicate()) {
          this.fetchTransactions();
          if (this.$toasted && this.$toasted.global && this.$toasted.global.success) {
            this.$toasted.global.success(successMessage);
          }
          return;
        }
        await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
        await this.fetchSummary();
      }
    },
    stripBillingQuery() {
      try {
        window.history.replaceState(null, "", stripBillingReturn(window.location.href));
      } catch (e) { /* cosmetic only */ }
    },
    money(amount) {
      return formatMoney(amount, this.summary && this.summary.currency);
    },
    txLabel(t) {
      if (!t) return "";
      if (t.kind === "purchase") return "Credit pack";
      if (t.kind === "adjust") return "Manual adjustment";
      if (t.kind === "refund") return "Refund";
      if (t.kind === "expire") return "Expired credits";
      if (t.kind === "grant") {
        if (t.reference === "welcome") return "Welcome bonus";
        if (t.reference && t.reference.indexOf("voucher:") === 0) return "Voucher";
        return "Monthly grant";
      }
      return t.kind || "";
    },
    toastError(err, fallback) {
      const data = err && err.response && err.response.data;
      const msg = (data && (data.message || data.error)) || fallback || "Billing request failed.";
      if (this.$toasted && this.$toasted.global && this.$toasted.global.error) {
        this.$toasted.global.error(err, msg);
      }
    },
    fmt(value) {
      const n = Number(value) || 0;
      if (Math.abs(n) >= 100) return Math.round(n).toLocaleString();
      return (Math.round(n * 10) / 10).toLocaleString();
    },
    fmtDate(value) {
      const d = new Date(value);
      return isNaN(d.getTime()) ? "" : d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
    },
    shortDate(value) {
      const d = new Date(value);
      return isNaN(d.getTime()) ? "" : d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
    },
    cap(s) {
      const v = String(s || "");
      return v.charAt(0).toUpperCase() + v.slice(1);
    },
  },
};
</script>

<style lang="scss" scoped>
.ws-billing-spinner {
  display: flex;
  justify-content: center;
  padding: 24px 0;
}
.wsb-banner {
  border: 1px solid #f0d9a8;
  background: #fdf6e3;
  color: #8a5208;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 13px;
  margin-bottom: 12px;
}
.wsb-plan-card {
  border: 1px solid var(--fts-border, #e5e7eb);
  border-radius: 12px;
  padding: 14px 16px;
  margin-bottom: 12px;
}
.wsb-plan-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.wsb-plan-name {
  font-weight: 600;
  font-size: 15px;
}
.wsb-status {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-radius: 999px;
  padding: 2px 10px;
  background: var(--fts-gray-100, #f3f4f6);
  &.is-active { background: #e2f1ee; color: #0a5c52; }
  &.is-past_due { background: #fdecea; color: #b3261e; }
  &.is-expired { background: #fdecea; color: #b3261e; }
}
.wsb-plan-sub {
  margin-top: 6px;
  font-size: 12.5px;
  color: var(--fts-gray-500, #6b7280);
}
.wsb-actions {
  margin-top: 10px;
}
.wsb-seats {
  margin-top: 10px;
  padding-top: 6px;
  border-top: 1px solid var(--fts-border, #e5e7eb);
}
.wsb-credits {
  border: 1px solid var(--fts-border, #e5e7eb);
  border-radius: 12px;
  padding: 10px 16px 12px;
  margin-bottom: 16px;
}
.wsb-credit-block {
  padding-bottom: 8px;
  border-bottom: 1px solid var(--fts-border, #e5e7eb);
  margin-bottom: 2px;
}
.wsb-credit-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  padding: 6px 0;
}
.wsb-credit-label {
  font-size: 13px;
  color: var(--fts-gray-500, #6b7280);
}
.wsb-credit-value {
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}
.wsb-credit-sub {
  font-weight: 400;
  font-size: 12px;
  color: var(--fts-gray-500, #6b7280);
}
.wsb-meter {
  height: 6px;
  border-radius: 999px;
  background: var(--fts-gray-100, #f3f4f6);
  overflow: hidden;
}
.wsb-meter-fill {
  height: 100%;
  border-radius: 999px;
  background: #0e7a6d;
  transition: width 0.3s ease;
  &.is-low { background: #b45309; }
}
.wsb-packs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}
.wsb-redeem {
  display: flex;
  gap: 8px;
  margin-top: 8px;
}
.wsb-redeem-input {
  flex: 1 1 auto;
  min-width: 0;
  height: 32px;
  padding: 0 10px;
  font-size: 13px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  border: 1px solid var(--fts-border, #e5e7eb);
  border-radius: 8px;
  background: #fff;
  &::placeholder { letter-spacing: normal; text-transform: none; }
  &:focus { outline: none; border-color: #5457c9; }
  &:disabled { background: var(--fts-gray-100, #f3f4f6); }
}
.wsb-section-title {
  font-weight: 600;
  font-size: 14px;
  margin: 0 0 8px;
}
.wsb-chart-card,
.wsb-history-card {
  border: 1px solid var(--fts-border, #e5e7eb);
  border-radius: 12px;
  padding: 12px 16px;
  margin-bottom: 16px;
}
.wsb-chart {
  width: 100%;
  height: 128px;
  display: block;
}
.wsb-axis {
  stroke: var(--fts-border, #e5e7eb);
  stroke-width: 1;
}
.wsb-axis-label {
  font-size: 10px;
  fill: var(--fts-gray-500, #6b7280);
}
.wsb-legend {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-top: 8px;
  font-size: 12px;
  color: var(--fts-gray-500, #6b7280);
}
.wsb-legend-item {
  display: inline-flex;
  align-items: center;
  gap: 5px;
}
.wsb-legend-dot {
  width: 8px;
  height: 8px;
  border-radius: 2px;
  display: inline-block;
}
.wsb-legend-total {
  margin-left: auto;
  font-variant-numeric: tabular-nums;
}
.wsb-tx-row {
  display: flex;
  align-items: baseline;
  gap: 12px;
  padding: 7px 0;
  font-size: 13px;
  & + .wsb-tx-row {
    border-top: 1px solid var(--fts-border, #e5e7eb);
  }
}
.wsb-tx-date {
  color: var(--fts-gray-500, #6b7280);
  min-width: 90px;
}
.wsb-tx-label {
  flex: 1;
}
.wsb-tx-delta {
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: #0a5c52;
  &.is-neg { color: #b3261e; }
}
.wsb-load-more {
  padding-top: 8px;
  text-align: center;
}
.wsb-note {
  font-size: 13px;
  color: var(--fts-gray-500, #6b7280);
}
.wsb-buy-btns {
  display: flex;
  gap: 8px;
  align-items: center;
}
.wsb-price-tag {
  font-weight: 600;
  font-size: 15px;
  font-variant-numeric: tabular-nums;
}
.wsb-price-unit {
  font-weight: 400;
  font-size: 12px;
  color: var(--fts-gray-500, #6b7280);
}
.wsb-price-sub {
  font-size: 12px;
  color: var(--fts-gray-500, #6b7280);
  font-variant-numeric: tabular-nums;
}
.wsb-pack-price {
  font-weight: 400;
  opacity: 0.8;
}
</style>
